import { ChevronRight, User } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  daysBetween,
  REPAIR_REQUEST_STATUS_LABELS,
  REPAIR_URGENCY_LABELS,
  isRepairRequestOpen,
  tidyText,
  today,
  toPlainDate,
} from '@cfl/assets-shared';

import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
} from '@/components/cargofl-ui';
import { FreeText } from '@/components/free-text';
import { PILL_CLASS, repairRequestVariant, urgencyVariant } from '@/lib/badges';
import { ApiError } from '@/lib/api';
import {
  useRepairRequestQueue,
  useReviewRepairRequest,
  type RepairRequest,
} from '@/lib/repair-requests-api';
import { useCan } from '@/lib/session';
import { AttachmentStrip } from '@/routes/report-problem';
import { cn } from '@/lib/utils';

/**
 * The review queue: what employees have reported, waiting on IT.
 *
 * Lives on the Repairs page rather than its own screen, because a report and the
 * repair it may become are the same piece of work seen at two moments, and splitting
 * them across two nav entries makes people check one and forget the other.
 */
export function RepairQueue() {
  const canReview = useCan('manageRepairs');
  const canSee = useCan('viewRegister');
  const { data: requests, isPending } = useRepairRequestQueue('', { enabled: canSee });
  const [reviewing, setReviewing] = React.useState<RepairRequest | null>(null);

  if (!canSee) return null;

  const open = requests?.filter((r) => isRepairRequestOpen(r.status)) ?? [];
  const closed = requests?.filter((r) => !isRepairRequestOpen(r.status)) ?? [];

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">
            Reported by employees
            {open.length > 0 && (
              <Badge variant="soft-warning" className={`${PILL_CLASS} ml-2`}>
                {open.length} waiting
              </Badge>
            )}
          </CardTitle>
          <CardDescription className="text-xs">
            Problems reported by whoever is holding the asset. Nothing here has moved the
            asset out of service — raising a repair is what does that.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          {isPending ? (
            <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>
          ) : open.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Nothing reported. Employees can report a problem from their own
              <Link to="/my-assets" className="mx-1 underline underline-offset-2">
                My assets
              </Link>
              screen.
            </p>
          ) : (
            <ul className="divide-y">
              {open.map((request) => (
                <RequestRow
                  key={request.id}
                  request={request}
                  onReview={canReview ? () => setReviewing(request) : undefined}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/*
        Shut by default, and the only section on this page that is.

        These are finished - nobody opens the Repairs screen to read them. Left
        expanded they sat between the reports WAITING on IT and the repairs actually
        in progress, so the live work was below the fold and the settled work was not.

        A <details> rather than a useState toggle: the browser already does this, it
        opens on Ctrl+F for text inside it, and it needs no JavaScript to be correct.
      */}
      {closed.length > 0 && (
        <Card>
          <details className="group">
            <summary className="flex cursor-pointer list-none items-center gap-2 p-6 text-sm font-semibold [&::-webkit-details-marker]:hidden">
              <ChevronRight className="size-4 text-muted-foreground transition-transform group-open:rotate-90" />
              Reports already dealt with
              <Badge variant="soft-muted" className={PILL_CLASS}>
                {closed.length}
              </Badge>
            </summary>
            <CardContent className="pt-0">
              <ul className="divide-y">
                {closed.map((request) => (
                  <RequestRow key={request.id} request={request} />
                ))}
              </ul>
            </CardContent>
          </details>
        </Card>
      )}

      <ReviewDialog request={reviewing} onClose={() => setReviewing(null)} />
    </>
  );
}

function RequestRow({
  request,
  onReview,
}: {
  request: RepairRequest;
  onReview?: () => void;
}) {
  // How long it has been waiting, not the date it was raised. A queue is worked
  // oldest-first and "3 days ago" answers that without the reader subtracting dates in
  // their head - the same reason every service desk shows an age column.
  const days = daysBetween(toPlainDate(new Date(request.createdAt)), today());
  const waiting = days === 0 ? 'today' : days === 1 ? 'yesterday' : `${days} days ago`;
  const photos = request.attachments.length;

  return (
    <li className="space-y-1.5 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <Link to={`/assets/${request.asset.id}`} className="at-num text-sm font-medium hover:underline">
          {request.asset.code}
        </Link>
        <span className="text-sm text-muted-foreground">{request.asset.name}</span>

        <Badge variant={urgencyVariant(request.urgency)} className={PILL_CLASS}>
          {REPAIR_URGENCY_LABELS[request.urgency]}
        </Badge>
        <Badge variant={repairRequestVariant(request.status)} className={PILL_CLASS}>
          {REPAIR_REQUEST_STATUS_LABELS[request.status]}
        </Badge>

        {onReview && (
          <Button hint={"Accept, decline or schedule this repair request"} size="sm" variant="secondary" className="ml-auto" onClick={onReview}>
            Review
          </Button>
        )}
      </div>

      <FreeText text={request.description} />

      {/* Who reported it is the first thing IT looks for, so it is not 11px grey
          like the rest of the meta - the name is read, the department is scanned.

          The photos are a count here rather than a strip of thumbnails. Three of them
          made a row four times the height of the one below it, which is what stopped
          the queue reading as a list; they are on the review dialog, where somebody is
          actually looking at the damage. */}
      <p className="flex flex-wrap items-center gap-x-1.5 text-[11px] text-muted-foreground">
        <User className="size-3.5" />
        <span className="text-xs font-semibold text-foreground">{request.reportedBy.name}</span>
        <span className="at-num">({request.reportedBy.employeeCode})</span>
        {request.reportedBy.department ? <span>· {request.reportedBy.department}</span> : null}
        <span>· raised {waiting}</span>
        {photos > 0 && <span>· {photos === 1 ? '1 photo' : `${photos} photos`}</span>}
      </p>

      <HandoverCondition handover={request.handover} quiet />

      {request.reviewNote && (
        <div className="rounded-md bg-muted/60 p-2.5">
          <FreeText
            text={request.reviewNote}
            accent={false}
            prefix={
              <span className="font-medium">{`${request.reviewedByName ?? 'IT'}: `}</span>
            }
          />
        </div>
      )}
    </li>
  );
}

const OUTCOMES = [
  {
    status: 'ACKNOWLEDGED' as const,
    title: 'Seen it, still deciding',
    blurb: 'Tells the reporter it has not been ignored. Changes nothing else.',
  },
  {
    status: 'REPAIR_RAISED' as const,
    title: 'Send it for repair',
    blurb:
      'Creates a repair and takes the asset out of service. This is the only outcome that moves the asset.',
  },
  {
    status: 'RESOLVED' as const,
    title: 'Sorted without a repair',
    blurb: 'A setting, a cable, a reboot. Closes the report with no vendor involved.',
  },
  {
    status: 'DECLINED' as const,
    title: 'No repair needed',
    blurb: 'Say why — the reporter reads this, and "no" with no reason is how people stop reporting things.',
  },
];

function ReviewDialog({
  request,
  onClose,
}: {
  request: RepairRequest | null;
  onClose: () => void;
}) {
  const review = useReviewRepairRequest(request?.id ?? '');
  const [status, setStatus] = React.useState<(typeof OUTCOMES)[number]['status'] | null>(null);
  const [note, setNote] = React.useState('');
  const [reason, setReason] = React.useState('');

  React.useEffect(() => {
    if (request) {
      setStatus(null);
      setNote('');
      setReason('');
    }
  }, [request]);

  const submit = async () => {
    if (!status) return;
    try {
      const result = await review.mutateAsync({
        status,
        note: note.trim() || null,
        reason: reason.trim() || null,
      });
      toast.success(
        result.repairId
          ? `${request?.asset.code} sent for repair and taken out of service.`
          : 'Report updated. The reporter can see it.',
      );
      onClose();
    } catch (error) {
      const message =
        error instanceof ApiError
          ? (Object.values(error.fieldErrors ?? {})[0]?.[0] ?? error.message)
          : 'That could not be saved.';
      toast.error(message);
    }
  };

  return (
    <Dialog open={request !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Review this report</DialogTitle>
          <DialogDescription>
            {request?.asset.code} — reported by {request?.reportedBy.name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <p className="whitespace-pre-wrap rounded-md bg-muted/60 p-3 text-xs">
            {request ? tidyText(request.description) : null}
          </p>

          <AttachmentStrip attachments={request?.attachments ?? []} />

          <HandoverCondition handover={request?.handover ?? null} />

          <div className="space-y-2">
            {OUTCOMES.map((outcome) => (
              <button
                key={outcome.status}
                type="button"
                onClick={() => setStatus(outcome.status)}
                className={cn(
                  'w-full rounded-md border p-3 text-left transition-colors',
                  status === outcome.status ? 'border-primary bg-accent' : 'hover:bg-muted/50',
                )}
              >
                <div className="text-sm font-medium">{outcome.title}</div>
                <div className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                  {outcome.blurb}
                </div>
              </button>
            ))}
          </div>

          {status === 'REPAIR_RAISED' && (
            <>
              <Alert className="border-warning/40 bg-warning/5">
                <AlertDescription className="text-xs">
                  {request?.asset.code} will move to Under repair and cannot be issued until
                  the repair is closed. Where it returns to afterwards is remembered.
                </AlertDescription>
              </Alert>
              <div className="space-y-1.5">
                <Label>Repair reason (optional)</Label>
                <Input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Defaults to the reporter's description"
                />
              </div>
            </>
          )}

          <div className="space-y-1.5">
            <Label>
              Note to the reporter{status === 'DECLINED' ? '' : ' (optional)'}
            </Label>
            <Textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={
                status === 'DECLINED'
                  ? 'Why no repair is needed — they will read this.'
                  : 'Anything they should know.'
              }
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!status || review.isPending || (status === 'DECLINED' && !note.trim())}
            onClick={() => void submit()}
          >
            {review.isPending ? 'Saving…' : 'Save'}
          </Button>
          {(!status || (status === 'DECLINED' && !note.trim())) && <span className="self-center text-xs text-muted-foreground">{!status ? 'Choose a decision first' : 'Add a note saying why it is declined'}</span>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * What was already wrong with this machine when the reporter got it.
 *
 * The whole reason the handover snapshot exists. Without it, "was the F5 key already
 * dead?" is settled by whoever sounds more certain, and a repair charge lands on
 * whoever argues less.
 *
 * Three states, and they must not be merged. "Not recorded" is MUTED, never red -
 * most handovers predate this feature and flagging every one of them as a problem is
 * exactly what trains people to stop reading the colour.
 */
function HandoverCondition({
  handover,
  quiet,
}: {
  handover: RepairRequest['handover'];
  /**
   * Drop the "nothing was recorded" line, for a list.
   *
   * Most handovers predate the condition check, so in the queue that sentence is on
   * nearly every row, identical each time, saying nothing that distinguishes one
   * report from the next - three of them on one screen is what made the queue read as
   * noise. It stays in the review dialog, where "there is nothing to compare against"
   * is a real part of the decision being made.
   */
  quiet?: boolean;
}) {
  if (!handover) return null;

  const issued = new Date(handover.assignedAt).toLocaleDateString();

  if (handover.checkedAt === null) {
    if (quiet) return null;
    return (
      <p className="rounded-md bg-muted/40 px-2.5 py-1.5 text-[11px] text-muted-foreground">
        Condition was not recorded when this was issued on{' '}
        <span className="at-num">{issued}</span> — there is nothing to compare against.
      </p>
    );
  }

  if (handover.faults.length === 0 && !handover.notes) {
    return (
      <p className="rounded-md bg-muted/40 px-2.5 py-1.5 text-[11px] text-muted-foreground">
        Checked and found clean when issued on <span className="at-num">{issued}</span>.
      </p>
    );
  }

  return (
    <div className="space-y-1 rounded-md border border-warning/40 bg-warning/5 px-2.5 py-2">
      <p className="text-[11px] font-medium">
        Already known when issued on <span className="at-num">{issued}</span>:
      </p>
      <div className="flex flex-wrap gap-1">
        {handover.faults.map((fault) => (
          <span
            key={fault}
            className="rounded-full border border-warning/40 px-2 py-0.5 text-[11px] text-warning"
          >
            {fault}
          </span>
        ))}
      </div>
      {handover.notes && (
        <p className="text-[11px] leading-relaxed text-muted-foreground">{handover.notes}</p>
      )}
    </div>
  );
}
